<template>
    <b-overlay>
        <div class="row">
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.totalBranches" text="Toplam Şube" icon="business" variant="primary" />
            </div>
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.totalVehicles" text="Toplam Araç Sayısı" icon="directions_car" variant="info" />
            </div>
            <div class="col-md-4 mb-1">
                <v-stats-box :value="summaryData.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })" text="Genel Toplam Litre" icon="local_gas_station" variant="success" />
            </div>
        </div>
        <div class="d-flex align-items-center mb-1 px-25">
            <feather-icon icon="FilterIcon" size="13" class="text-muted mr-50" />
            <small class="text-muted">
                <strong>Uygulanan Tarih Aralığı:</strong>
                {{ appliedFilters.startDate ? formatDate(appliedFilters.startDate) : 'Başlangıç tarihi yok' }}
                —
                {{ appliedFilters.endDate ? formatDate(appliedFilters.endDate) : 'Bitiş tarihi yok' }}
                <span v-if="!appliedFilters.startDate && !appliedFilters.endDate" class="text-warning font-weight-bold ml-25">(Tüm tarihler gösterilmektedir)</span>
            </small>
        </div>
        <b-card no-body>
            <v-table ref="reportTable" :items-provider="fetchReport" :editor="processReportData" :fields="reportFields" :busy.sync="reportLoading" pagination="client" search>
                <template #filters>
                    <div class="row align-items-end mb-0">
                        <div class="col-md-3 col-6 mb-1">
                            <label class="form-label">Başlangıç Tarihi</label>
                            <v-date-picker v-model="reportFilters.startDate" placeholder="Başlangıç" />
                        </div>
                        <div class="col-md-3 col-6 mb-1">
                            <label class="form-label">Bitiş Tarihi</label>
                            <v-date-picker v-model="reportFilters.endDate" placeholder="Bitiş" />
                        </div>
                        <div class="col-md-3 col-12 mb-1">
                            <label class="form-label">Şube</label>
                            <v-select-list v-model="reportFilters.branchIds" :options="branches" label="Name" :reduce="(branch) => branch.Id" placeholder="Tümü" multiple />
                        </div>
                        <div class="col-md-3 col-12 mb-1 d-flex justify-content-end align-items-end">
                            <button class="btn btn-primary" @click="applyFilter" :disabled="reportLoading">
                                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                                Filtrele
                            </button>
                            <button class="btn btn-outline-secondary ml-50" @click="clearFilters">
                                <feather-icon icon="XIcon" size="14" class="mr-50" />
                                Temizle
                            </button>
                        </div>
                    </div>
                </template>
                <template #additional-actions>
                    <div class="d-flex justify-content-end">
                        <button v-b-tooltip.hover.bottom title="Dışa Aktar" class="btn btn-sm btn-icon rounded-circle btn-outline-success p-25" v-on:click="exportToExcel" :disabled="reportLoading">
                            <feather-icon icon="ArrowDownIcon" size="12" />
                        </button>
                    </div>
                </template>
            </v-table>
        </b-card>
    </b-overlay>
</template>

<script>
import api from "@/api/fuel-storage";
import CompanyApi from "@/api/company";
import { BCard, BOverlay, VBTooltip } from "bootstrap-vue";
import VStatsBox from "@/components/VStatsBox.vue";
import xlsx from "json-as-xlsx";

const getInitialFilters = () => ({
    startDate: null,
    endDate: null,
    branchIds: [],
});

export default {
    name: "FuelConsumptionByBranchView",
    components: { BCard, VStatsBox, BOverlay },
    directives: {
        "b-tooltip": VBTooltip,
    },
    data() {
        return {
            summaryData: {
                totalBranches: 0,
                totalVehicles: 0,
                grandTotal: 0,
            },
            reportFilters: getInitialFilters(),
            appliedFilters: getInitialFilters(),
            reportLoading: false,
            branches: [],
            reportFields: [
                { key: "siraNo", name: "S.No", thClass: "text-nowrap", tdClass: "text-nowrap" },
                { key: "BranchName", name: "Şube", thClass: "text-nowrap", tdClass: "text-nowrap" },
                { key: "VehicleCount", name: "Araç Sayısı", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "DepotMotorinStr", name: "Depo Motorin [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "DepotAdblueStr", name: "Depo AdBlue [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "TotalLtStr", name: "Total [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "PoLtStr", name: "PO [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "ExternalLtStr", name: "Dışarıdan [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
                { key: "GrandTotalStr", name: "Genel Toplam [lt]", thClass: "text-nowrap", tdClass: "text-nowrap text-right font-weight-bold" },
                { key: "TotalCostStr", name: "Toplam Tutar (₺)", thClass: "text-nowrap", tdClass: "text-nowrap text-right" },
            ],
        };
    },
    created() {
        this.loadFilterOptions();
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const [year, month, day] = dateStr.split("-");
            return `${day}.${month}.${year}`;
        },
        applyFilter() {
            this.$refs.reportTable.refresh();
        },
        exportToExcel() {
            const table = this.$refs.reportTable;
            const tableData = table?.$data?.d_items || [];
            if (tableData.length === 0) {
                this.$toast.warning("Dışa aktarılacak veri bulunamadı");
                return;
            }
            this.generateExcelFile(tableData);
        },
        generateExcelFile(data) {
            const columns = [
                { label: "S.No", value: "siraNo" },
                { label: "Şube", value: "BranchName" },
                { label: "Araç Sayısı", value: "VehicleCount" },
                { label: "Depo Motorin [lt]", value: "DepotMotorinStr" },
                { label: "Depo AdBlue [lt]", value: "DepotAdblueStr" },
                { label: "Total [lt]", value: "TotalLtStr" },
                { label: "PO [lt]", value: "PoLtStr" },
                { label: "Dışarıdan [lt]", value: "ExternalLtStr" },
                { label: "Genel Toplam [lt]", value: "GrandTotalStr" },
                { label: "Toplam Tutar (₺)", value: "TotalCostStr" },
            ];
            const content = data.map((item) => ({
                siraNo: item.siraNo || "-",
                BranchName: item.BranchName || "-",
                VehicleCount: item.VehicleCount || 0,
                DepotMotorinStr: item.DepotMotorinStr || "0",
                DepotAdblueStr: item.DepotAdblueStr || "0",
                TotalLtStr: item.TotalLtStr || "0",
                PoLtStr: item.PoLtStr || "0",
                ExternalLtStr: item.ExternalLtStr || "0",
                GrandTotalStr: item.GrandTotalStr || "0",
                TotalCostStr: item.TotalCostStr || "0",
            }));
            const settings = {
                fileName: `Sube_Bazli_Yakit_Alimlari_${new Date().toISOString().slice(0, 10)}`,
                extraLength: 3,
                writeMode: "writeFile",
            };
            xlsx([{ sheet: "Şube Bazlı Yakıt Alımları", columns, content }], settings);
        },
        loadFilterOptions() {
            this.loadBranches();
        },
        clearFilters() {
            this.reportFilters = getInitialFilters();
            this.$nextTick(() => this.$refs.reportTable.refresh());
        },
        fetchReport() {
            this.appliedFilters = { ...this.reportFilters };
            const params = {
                ...this.reportFilters,
                startDate: this.reportFilters.startDate ? `${this.reportFilters.startDate}T00:00:00` : null,
                endDate: this.reportFilters.endDate ? `${this.reportFilters.endDate}T23:59:59` : null,
            };
            return api.FuelConsumptionReport.GetByBranch(params);
        },
        processReportData(response) {
            if (!response?.Success) {
                this.$result({ data: response });
                this.summaryData = { totalBranches: 0, totalVehicles: 0, grandTotal: 0 };
                return [];
            }
            const data = response.Data || [];
            this.summaryData = {
                totalBranches: data.length,
                totalVehicles: data.reduce((sum, item) => sum + (item.VehicleCount || 0), 0),
                grandTotal: data.reduce((sum, item) => sum + (item.GrandTotal || 0), 0),
            };
            return data.map((item, index) => ({
                ...item,
                siraNo: index + 1,
            }));
        },
        loadBranches() {
            CompanyApi.Branch.ListByCode("O").then((res) => {
                if (res.data.Success) this.branches = res.data.Data || [];
            });
        },
    },
};
</script>
